import { useEffect, useState } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Bug, CircleHelp, Lightbulb, X } from 'lucide-react';
import {
  Alert,
  Button,
  GfmMarkdown,
  Icon,
  IconButton,
  Select,
  Snackbar,
  Text,
  TextArea,
  TextInput,
  typography,
  cx,
} from '../../components/ui';
import type { SelectOption } from '../../components/ui';
import type {
  FeedbackInput,
  FeedbackResult,
  FeedbackType,
} from './useFeedbackApi';

// Each type carries a lucide icon tinted from a foundation semantic token —
// red for bugs, amber for ideas, brand for questions — shown in both the
// trigger and the dropdown list.
const TYPE_OPTIONS: (SelectOption & { value: FeedbackType })[] = [
  {
    value: 'bug',
    label: 'Bug',
    icon: <Icon as={Bug} size="sm" className="text-error" />,
  },
  {
    value: 'enhancement',
    label: 'Idea / feature request',
    icon: <Icon as={Lightbulb} size="sm" className="text-warning-500" />,
  },
  {
    value: 'question',
    label: 'Question',
    icon: <Icon as={CircleHelp} size="sm" className="text-brand-text" />,
  },
];

export interface FeedbackDialogViewProps {
  open: boolean;
  onClose: () => void;
  /** Current page URL, pre-filled into the (editable) Page field. */
  defaultPageUrl: string;
  onSubmit: (input: FeedbackInput) => Promise<FeedbackResult>;
}

/**
 * Presentational feedback modal — composes the design-system primitives only
 * (no Backstage imports, so it's Storybook-safe). Owns the form + submit state;
 * `onSubmit` is injected so the connected wrapper can supply the real API and
 * stories can supply a fake. On success it closes the modal and raises a
 * success Snackbar with a link to the created issue; on failure it keeps the
 * modal open with an inline Alert so the user can retry without re-typing.
 */
export const FeedbackDialogView = ({
  open,
  onClose,
  defaultPageUrl,
  onSubmit,
}: FeedbackDialogViewProps) => {
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pageUrl, setPageUrl] = useState(defaultPageUrl);
  const [titleError, setTitleError] = useState<string>();
  const [bodyError, setBodyError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<FeedbackResult>();
  const [descMode, setDescMode] = useState<'write' | 'preview'>('write');

  // Reset the form each time the modal opens (and refresh the prefilled URL).
  useEffect(() => {
    if (!open) return;
    setType('bug');
    setTitle('');
    setBody('');
    setPageUrl(defaultPageUrl);
    setTitleError(undefined);
    setBodyError(undefined);
    setSubmitError(undefined);
    setSubmitting(false);
    setDescMode('write');
  }, [open, defaultPageUrl]);

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    const tErr = trimmedTitle ? undefined : 'A title is required.';
    const bErr = trimmedBody ? undefined : 'A description is required.';
    setTitleError(tErr);
    setBodyError(bErr);
    if (tErr || bErr) return;

    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const result = await onSubmit({
        type,
        title: trimmedTitle,
        body: trimmedBody,
        pageUrl: pageUrl.trim(),
      });
      setCreated(result);
      onClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalOverlay
        isOpen={open}
        onOpenChange={isOpen => {
          if (!isOpen) onClose();
        }}
        isDismissable={!submitting}
        isKeyboardDismissDisabled={submitting}
        className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
      >
        <Modal className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-default bg-surface shadow-lg outline-none">
          <Dialog className="flex min-h-0 flex-1 flex-col p-5 outline-none">
            <div className="flex items-start justify-between gap-3">
              <Heading slot="title" className={cx(typography.title, 'text-fg')}>
                Send feedback
              </Heading>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Close"
                onPress={onClose}
                isDisabled={submitting}
                className="-mr-1.5 -mt-1 shrink-0"
              >
                <Icon as={X} size="md" />
              </IconButton>
            </div>
            <Text variant="body" tone="secondary" className="mt-1">
              Report a bug, suggest an idea, or ask a question. This opens a
              GitHub issue for the team.
            </Text>

            {/* Fields scroll when the form (e.g. a long description) outgrows
                the capped dialog height; the header and footer stay put. The
                -mr-5/pr-5 bleeds the scroll viewport to the dialog's right edge
                so the scrollbar hugs the edge while content stays aligned. */}
            <div className="-mr-5 mt-5 flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto pr-5">
              {submitError && (
                <Alert severity="error" title="Couldn’t send feedback">
                  {submitError}
                </Alert>
              )}

              <Select
                label="Type"
                options={TYPE_OPTIONS}
                value={type}
                onChange={value => setType(value as FeedbackType)}
                isRequired
                fullWidth
              />

              <TextInput
                label="Title"
                placeholder="A short summary"
                value={title}
                onChange={value => {
                  setTitle(value);
                  if (titleError) setTitleError(undefined);
                }}
                errorMessage={titleError}
                isRequired
                fullWidth
              />

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className={cx(typography.label, 'text-fg-secondary')}>
                    Description
                    <span className="ml-0.5 text-brand-text">*</span>
                  </span>
                  <div className="flex items-center gap-1">
                    {(['write', 'preview'] as const).map(mode => (
                      <Button
                        key={mode}
                        variant={descMode === mode ? 'tertiary' : 'ghost'}
                        size="sm"
                        className="h-7 px-2.5 capitalize"
                        onPress={() => setDescMode(mode)}
                      >
                        {mode}
                      </Button>
                    ))}
                  </div>
                </div>
                {descMode === 'write' ? (
                  <TextArea
                    aria-label="Description"
                    placeholder="What happened, or what would you like to see?"
                    value={body}
                    onChange={value => {
                      setBody(value);
                      if (bodyError) setBodyError(undefined);
                    }}
                    errorMessage={bodyError}
                    hint="Markdown is supported — preview it with the toggle above."
                    rows={5}
                    autoResize
                    isRequired
                    fullWidth
                  />
                ) : (
                  <div className="min-h-32 rounded-lg border border-strong bg-surface px-3 py-2.5">
                    {body.trim() ? (
                      <GfmMarkdown>{body}</GfmMarkdown>
                    ) : (
                      <Text variant="body" tone="tertiary">
                        Nothing to preview yet.
                      </Text>
                    )}
                  </div>
                )}
              </div>

              <TextInput
                label="Page"
                value={pageUrl}
                onChange={setPageUrl}
                hint="Where you were — edit if this isn’t the right page."
                fullWidth
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                isDisabled={submitting}
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isDisabled={submitting}
                onPress={handleSubmit}
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <Snackbar
        open={Boolean(created)}
        severity="success"
        title="Feedback sent"
        autoHideDuration={8000}
        onClose={() => setCreated(undefined)}
        action={
          created
            ? {
                label: `View issue #${created.number}`,
                onPress: () =>
                  window.open(created.issueUrl, '_blank', 'noopener'),
              }
            : undefined
        }
      >
        {created
          ? `Issue #${created.number} was created. Thank you!`
          : undefined}
      </Snackbar>
    </>
  );
};
